import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { ObnlReviewStatus, Profile } from '../types/profile';
import { ServiceCategory } from '../types/service';
import { Industry } from './industryStore';
import { AdminReviewType } from '../types/review';
import { Report } from '../types/report';
import { Job, JobUpsertParams } from '../types/job';
import { JobClient, JobStatus } from '../types/common';
import { AdminCreditTransaction } from '../types/credits';
import { AdminUserLookupResult } from '../types/admin_user_lookup';

export interface AdminCity {
  id: number;
  name: string;
  code: string;
  latitude: number | null;
  longitude: number | null;
  created_at?: string;
  updated_at?: string;
}

// Types existants
interface FetchParams {
  limitCount?: number;
  offsetCount?: number;
  /** Used by fetchCities: filter by city name or code (server-side). */
  search?: string;
}

interface FetcReportsParams {
  limitCount?: number;
  offsetCount?: number;
  filters?: {
    status?: string;
    content_type?: string;
  };
}

interface JobFilters {
  status?: string;
  visibility?: string;
  search?: string;
  publication?: string;
}

export interface AdminShadowJob extends Job {
  shadow_recruiter_id?: string | null;
  is_shadow_recruiter?: boolean;
  shadow_recruiter_company_name?: string | null;
  recruiter_display_name?: string | null;
  recruiter_display_context?: string | null;
  // admin_fetch_jobs/admin_fetch_jobs_by_id return the client's full profile
  // row (to_jsonb(p.*)) as `client`, so `role` is present at runtime even
  // though the base JobClient type (shared with non-admin contexts) doesn't
  // declare it.
  client: JobClient & { role?: string | null };
}

interface AdminJobDeadlineUpdate {
  status: JobStatus;
  expires_at: string | null;
  selection_date: string | null;
}

export interface AdminRecruiterOption {
  id: string;
  first_name: string;
  last_name?: string | null;
  email?: string | null;
  company_name?: string | null;
}

export interface AdminShadowRecruiterJob {
  id: string;
  title: string;
  status?: string | null;
  visibility?: string | null;
}

export interface AdminShadowRecruiterInvite {
  shadow_recruiter_id: string;
  token: string;
  invite_url: string;
  expires_at?: string | null;
}

export interface AdminShadowRecruiter {
  id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  company_name?: string | null;
  jobs: AdminShadowRecruiterJob[];
  job_ids: string[];
  latest_invite?: AdminShadowRecruiterInvite | null;
  invited_at?: string | null;
  claimed_at?: string | null;
}

export interface AdminShadowRecruiterUpsertInput {
  id?: string | null;
  first_name: string;
  last_name: string;
  email?: string | null;
  company_name?: string | null;
  job_ids?: string[];
}

export interface FetchJobParams {
  limitCount?: number;
  offsetCount?: number;
  isLoadMore?: boolean;
  filters?: JobFilters;
}

interface ProfileFilters {
  role: string;
  status: string;
  city: string;
  obnl_review_status: string;
  search: string;
}

export interface FetchProfileParams {
  limitCount?: number;
  offsetCount?: number;
  filters?: ProfileFilters;
  isLoadMore?: boolean;
  queryKey?: string;
}

export interface CreditTransactionFilters {
  search: string;
  actionType: string;
  startDate: string;
  endDate: string;
  userId: string;
}

export interface FetchCreditTransactionParams {
  limitCount?: number;
  offsetCount?: number;
  filters?: CreditTransactionFilters;
  isLoadMore?: boolean;
}

export interface DashboardStats {
  total_reports: number | null;
  total_jobs: number | null;
  total_recruiters: number | null;
  total_freelancers: number | null;
  jobs_open: number | null;
  jobs_in_progress: number | null;
  jobs_completed: number | null;
  jobs_cancelled: number | null;
  jobs_paused: number | null;
  reports_pending: number | null;
  reports_resolved: number | null;
  reports_dismissed: number | null;
}

interface AdminStore {
  dashboard_stats: DashboardStats;

  profileFilters: ProfileFilters;
  jobFilters: JobFilters;
  creditTransactionFilters: CreditTransactionFilters;

  industries: Industry[];
  cities: AdminCity[];
  /** Current cities list search query (persisted for load-more). */
  citiesSearch: string;
  serviceCategories: ServiceCategory[];
  profiles: Profile[];
  reports: Report[];
  reviews: AdminReviewType[];
  jobs: AdminShadowJob[];
  creditTransactions: AdminCreditTransaction[];
  shadowRecruiters: AdminShadowRecruiter[];
  shadowRecruiterInvite: AdminShadowRecruiterInvite | null;

  /** Identifies the filters/search used for the current `profiles` list (for cache reuse). */
  profilesQueryKey: string | null;
  /** Last scroll position of the admin profile list, restored on return navigation. */
  profileListScrollY: number;

  industriesOffset: number;
  citiesOffset: number;
  serviceCategoriesOffset: number;
  profilesOffset: number;
  reportsOffset: number;
  reviewsOffset: number;
  jobsOffset: number;
  creditTransactionsOffset: number;

  limit: number;
  hasMore: {
    industries: boolean;
    cities: boolean;
    serviceCategories: boolean;
    profiles: boolean;
    reports: boolean;
    reviews: boolean;
    jobs: boolean;
    creditTransactions: boolean;
  };

  fetchIndustries: (params?: FetchParams) => Promise<void>;
  fetchCities: (params?: FetchParams) => Promise<void>;
  fetchServiceCategories: (params?: FetchParams) => Promise<void>;
  fetchProfiles: (params?: FetchProfileParams) => Promise<void>;
  fetchReports: (params?: FetcReportsParams) => Promise<void>;
  fetchReviews: (params?: FetchParams) => Promise<void>;
  /** Resolves to false (rather than rejecting) if the RPC call fails, so callers can detect a failed refresh without relying on a rejected promise. */
  fetchJobs: (params?: FetchJobParams) => Promise<boolean>;
  /** Looks up a single job by id, independent of the current jobFilters/pagination. */
  fetchJobById: (jobId: string) => Promise<AdminShadowJob | null>;
  fetchCreditTransactions: (params?: FetchCreditTransactionParams) => Promise<void>;
  fetchShadowRecruiters: () => Promise<void>;

  fetchMoreIndustries: () => Promise<void>;
  fetchMoreCities: () => Promise<void>;
  fetchMoreServiceCategories: () => Promise<void>;
  fetchMoreProfiles: () => Promise<void>;
  fetchMoreReports: () => Promise<void>;
  fetchMoreReviews: () => Promise<void>;
  fetchMoreJobs: () => Promise<void>;
  fetchMoreCreditTransactions: () => Promise<void>;

  fetchStats: () => Promise<void>;

  setProfileListScrollY: (y: number) => void;

  updateReportStatus: (
    reportId: string,
    status: Report['status'],
    adminNotes?: string
  ) => Promise<void>;
  updateProfileRole: (profileId: string, role: string) => Promise<void>;
  updateProfileStatus: (profileId: string, status: string) => Promise<void>;
  anonymizeUser: (profileId: string) => Promise<void>;
  lookupUserByEmail: (email: string) => Promise<AdminUserLookupResult | null>;
  generateAuthLink: (
    userId: string
  ) => Promise<{ actionLink: string; linkType: 'magiclink' | 'recovery' }>;
  updateObnlReviewStatus: (profileId: string, reviewStatus: ObnlReviewStatus) => Promise<void>;
  updateReviewStatus: (reviewId: string, validated: boolean) => Promise<void>;
  updateJobStatus: (jobId: string, status: string) => Promise<void>;
  updateJobExpiresAt: (jobId: string, expiresAt: string) => Promise<void>;
  updateJobSelectionDate: (jobId: string, selectionDate: string | null) => Promise<void>;
  upsertShadowRecruiter: (input: AdminShadowRecruiterUpsertInput) => Promise<AdminShadowRecruiter>;
  issueShadowRecruiterInvite: (shadowRecruiterId: string) => Promise<AdminShadowRecruiterInvite>;
  adminUpsertJob: (jobId: string, params: JobUpsertParams, reason: string) => Promise<void>;
  adminUnpublishJob: (jobId: string, reason: string) => Promise<'pause' | 'unpublish'>;
  adminPublishJob: (
    jobId: string,
    listingTypeId: number | null,
    expiresAt: string | null,
    selectionDate?: string | null
  ) => Promise<boolean>;
  /** Typeahead search for active recruiter profiles; deliberately does not write `profiles`. */
  searchRecruiterProfiles: (search: string) => Promise<AdminRecruiterOption[]>;
  assignJobToRecruiter: (jobId: string, profileId: string) => Promise<boolean>;
  forceNotifyMatchingFreelancers: (jobId: string) => Promise<number>;
  approveSuggestedCategory: (
    categoryId: number,
    labels?: { labelFr: string; labelEn?: string | null }
  ) => Promise<ServiceCategory>;
  createAdminProfile: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    languages: string[],
    cityId: number,
    role: string,
    avatarUrl?: string,
    phoneNumber?: string
  ) => Promise<string>;

  deleteServiceCategory: (categoryId: number) => Promise<void>;
  updateServiceCategory: (
    categoryId: number,
    updates: { labelFr: string; labelEn?: string | null; description?: string | null }
  ) => Promise<ServiceCategory>;
  deleteIndustry: (id: number) => Promise<void>;

  insertCity: (params: {
    name: string;
    code: string;
    latitude: number | null;
    longitude: number | null;
  }) => Promise<void>;
}

const DEFAULT_LIMIT = 10;
type AdminRpcRow<T> = {
  item: T;
  total_count: number;
};

const isAdminRpcRow = <T>(value: unknown): value is AdminRpcRow<T> =>
  typeof value === 'object' && value !== null && 'item' in value;

const jobStatuses: readonly JobStatus[] = [
  'open',
  'in_progress',
  'completed',
  'cancelled',
  'paused',
  'expired',
  'reviewing_quotes',
];

const isAdminJobDeadlineUpdate = (value: unknown): value is AdminJobDeadlineUpdate => {
  if (typeof value !== 'object' || value === null) return false;

  const record = value as Record<string, unknown>;
  return (
    typeof record.status === 'string' &&
    jobStatuses.includes(record.status as JobStatus) &&
    (typeof record.expires_at === 'string' || record.expires_at === null) &&
    (typeof record.selection_date === 'string' || record.selection_date === null)
  );
};

const extractAdminJobDeadlineUpdate = (data: unknown): AdminJobDeadlineUpdate | null => {
  const value = Array.isArray(data) ? data[0] : data;
  return isAdminJobDeadlineUpdate(value) ? value : null;
};

const extractAdminRpcItems = <T>(data: unknown): T[] => {
  if (!Array.isArray(data)) return [];
  if (data.length > 0 && isAdminRpcRow<T>(data[0])) {
    return (data as AdminRpcRow<T>[]).map(row => row.item);
  }
  return data as T[];
};

const toAbsoluteInviteUrl = (inviteUrl: string | null | undefined): string => {
  if (!inviteUrl) return '';
  if (/^https?:\/\//i.test(inviteUrl)) {
    return inviteUrl;
  }
  if (typeof window === 'undefined') {
    return inviteUrl;
  }
  return new URL(inviteUrl, window.location.origin).toString();
};

const normalizeShadowRecruiter = (value: unknown): AdminShadowRecruiter => {
  const recruiter = (value ?? {}) as Partial<AdminShadowRecruiter> & {
    jobs?: Array<Partial<AdminShadowRecruiterJob>> | null;
    job_ids?: string[] | null;
    latest_invite?: Partial<AdminShadowRecruiterInvite> | null;
  };
  const jobs = Array.isArray(recruiter.jobs)
    ? recruiter.jobs
        .filter(job => Boolean(job?.id && job?.title))
        .map(job => ({
          id: String(job.id),
          title: String(job.title),
          status: job.status ?? null,
          visibility: job.visibility ?? null,
        }))
    : [];

  const jobIds = Array.isArray(recruiter.job_ids)
    ? recruiter.job_ids.filter((jobId): jobId is string => typeof jobId === 'string')
    : jobs.map(job => job.id);

  const latestInvite =
    recruiter.latest_invite && recruiter.latest_invite.token && recruiter.latest_invite.invite_url
      ? {
          shadow_recruiter_id: recruiter.latest_invite.shadow_recruiter_id ?? String(recruiter.id),
          token: recruiter.latest_invite.token,
          invite_url: toAbsoluteInviteUrl(recruiter.latest_invite.invite_url),
          expires_at: recruiter.latest_invite.expires_at ?? null,
        }
      : null;

  return {
    id: String(recruiter.id ?? ''),
    first_name: recruiter.first_name ?? '',
    last_name: recruiter.last_name ?? '',
    email: recruiter.email ?? null,
    company_name: recruiter.company_name ?? null,
    jobs,
    job_ids: jobIds,
    latest_invite: latestInvite,
    invited_at: recruiter.invited_at ?? null,
    claimed_at: recruiter.claimed_at ?? null,
  };
};

const normalizeShadowRecruiterInvite = (
  shadowRecruiterId: string,
  value: unknown
): AdminShadowRecruiterInvite => {
  const invite = (value ?? {}) as Partial<AdminShadowRecruiterInvite>;

  return {
    shadow_recruiter_id: invite.shadow_recruiter_id ?? shadowRecruiterId,
    token: invite.token ?? '',
    invite_url: toAbsoluteInviteUrl(invite.invite_url),
    expires_at: invite.expires_at ?? null,
  };
};

export const useAdminStore = create<AdminStore>((set, get) => ({
  dashboard_stats: {
    total_reports: null,
    total_jobs: null,
    total_recruiters: null,
    total_freelancers: null,
    jobs_open: null,
    jobs_in_progress: null,
    jobs_completed: null,
    jobs_cancelled: null,
    jobs_paused: null,
    reports_pending: null,
    reports_resolved: null,
    reports_dismissed: null,
  },

  profileFilters: { role: '', status: '', city: '', obnl_review_status: '', search: '' },
  jobFilters: { status: '', visibility: '', search: '', publication: '' },
  creditTransactionFilters: { search: '', actionType: '', startDate: '', endDate: '', userId: '' },

  // Données
  industries: [],
  cities: [],
  citiesSearch: '',
  serviceCategories: [],
  profiles: [],
  reports: [],
  reviews: [],
  jobs: [],
  creditTransactions: [],
  shadowRecruiters: [],
  shadowRecruiterInvite: null,

  profilesQueryKey: null,
  profileListScrollY: 0,

  // Pagination
  industriesOffset: 0,
  citiesOffset: 0,
  serviceCategoriesOffset: 0,
  profilesOffset: 0,
  reportsOffset: 0,
  reviewsOffset: 0,
  jobsOffset: 0,
  creditTransactionsOffset: 0,
  limit: DEFAULT_LIMIT,

  hasMore: {
    industries: true,
    cities: true,
    serviceCategories: true,
    profiles: true,
    reports: true,
    reviews: true,
    jobs: true,
    creditTransactions: true,
  },

  setProfileListScrollY: (y: number) => {
    set({ profileListScrollY: y });
  },

  // Fetch des stats
  fetchStats: async () => {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_stats');

      if (error) throw error;

      set({
        dashboard_stats: data[0],
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  // Fetch des industries
  fetchIndustries: async (params: FetchParams = {}) => {
    try {
      const limit = params.limitCount || DEFAULT_LIMIT;
      const offset = params.offsetCount || 0;

      const { data, error } = await supabase.rpc('admin_fetch_industries', {
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      const rows = (data || []) as AdminRpcRow<Industry>[];
      const totalCount = rows[0]?.total_count || 0;

      set({
        industries: rows.map(row => row.item),
        industriesOffset: offset + limit,
        hasMore: { ...get().hasMore, industries: totalCount > offset + limit },
      });
    } catch (error) {
      console.error('Error fetching industries:', error);
      throw error;
    }
  },

  fetchCities: async (params: FetchParams = {}) => {
    try {
      const limit = params.limitCount || DEFAULT_LIMIT;
      const offset = params.offsetCount || 0;
      const nextSearch = params.search !== undefined ? params.search : get().citiesSearch;
      if (params.search !== undefined) {
        set({ citiesSearch: params.search });
      }

      const { data, error } = await supabase.rpc('admin_fetch_cities', {
        p_limit: limit,
        p_offset: offset,
        p_search: nextSearch.trim() === '' ? null : nextSearch.trim(),
      });

      if (error) throw error;
      const rows = (data || []) as AdminRpcRow<AdminCity>[];
      const totalCount = rows[0]?.total_count || 0;

      set({
        cities: rows.map(row => row.item),
        citiesOffset: offset + limit,
        hasMore: { ...get().hasMore, cities: totalCount > offset + limit },
      });
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    }
  },

  // Fetch des catégories de service
  fetchServiceCategories: async (params: FetchParams = {}) => {
    try {
      const limit = params.limitCount || DEFAULT_LIMIT;
      const offset = params.offsetCount || 0;

      const { data, error } = await supabase.rpc('admin_fetch_service_categories', {
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      const rows = (data || []) as AdminRpcRow<ServiceCategory>[];
      const totalCount = rows[0]?.total_count || 0;

      set({
        serviceCategories: rows.map(row => row.item),
        serviceCategoriesOffset: offset + limit,
        hasMore: { ...get().hasMore, serviceCategories: totalCount > offset + limit },
      });
    } catch (error) {
      console.error('Error fetching service categories:', error);
      throw error;
    }
  },

  fetchProfiles: async (params: FetchProfileParams = {}) => {
    try {
      const { limitCount, offsetCount, isLoadMore = false } = params;
      const limit = limitCount || 10;
      const offset = offsetCount || 0;

      let activeFilters = params.filters || get().profileFilters;

      if (!isLoadMore && params.filters) {
        set({ profileFilters: params.filters });
        activeFilters = params.filters;
      }

      const { data, error } = await supabase.rpc('admin_fetch_profiles', {
        p_limit: limit,
        p_offset: offset,
        p_role: activeFilters.role || null,
        p_status: activeFilters.status || null,
        p_city: activeFilters.city || null,
        p_obnl_review_status: activeFilters.obnl_review_status || null,
        p_search: activeFilters.search.trim() || null,
      });

      if (error) throw error;
      const rows = (data || []) as AdminRpcRow<
        Profile & {
          services: { service_category: ServiceCategory }[];
          industries?: Industry[];
        }
      >[];
      const totalCount = rows[0]?.total_count || 0;
      const items = rows.map(row => row.item);

      const transformedProfiles = items.map(profile => ({
        ...profile,
        services: (profile.services || []).map(
          (service: { service_category: ServiceCategory }) => service.service_category
        ),
        industries: profile.industries?.map((pi: Industry) => pi) || [],
      }));

      set(state => ({
        profiles: isLoadMore ? [...state.profiles, ...transformedProfiles] : transformedProfiles,
        profilesOffset: offset + limit,
        hasMore: { ...state.hasMore, profiles: totalCount > offset + limit },
        ...(isLoadMore ? {} : { profilesQueryKey: params.queryKey ?? null }),
      }));
    } catch (error) {
      console.error('Error fetching profiles:', error);
    }
  },

  fetchReports: async (params: FetcReportsParams = {}) => {
    const { filters = {} } = params;
    const limit = params.limitCount || 10;
    const offset = params.offsetCount || 0;

    const { data, error } = await supabase.rpc('admin_fetch_reports', {
      p_limit: limit,
      p_offset: offset,
      p_status: filters.status || null,
      p_content_type: filters.content_type || null,
    });
    if (error) throw error;
    const rows = (data || []) as AdminRpcRow<Report>[];
    const totalCount = rows[0]?.total_count || 0;

    set({
      reports: rows.map(row => row.item),
      reportsOffset: offset + limit,
      hasMore: { ...get().hasMore, reports: totalCount > offset + limit },
    });
  },

  // Fetch des avis avec informations des utilisateurs
  fetchReviews: async (params: FetchParams = {}) => {
    try {
      const limit = params.limitCount || DEFAULT_LIMIT;
      const offset = params.offsetCount || 0;

      const { data, error } = await supabase.rpc('admin_fetch_reviews', {
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      const rows = (data || []) as AdminRpcRow<AdminReviewType>[];
      const totalCount = rows[0]?.total_count || 0;

      set({
        reviews: rows.map(row => row.item),
        reviewsOffset: offset + limit,
        hasMore: { ...get().hasMore, reviews: totalCount > offset + limit },
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },

  fetchJobs: async (params: FetchJobParams = {}) => {
    try {
      const { limitCount, offsetCount, isLoadMore = false } = params;
      const limit = limitCount || 10;
      const offset = offsetCount || 0;

      let activeFilters = params.filters || get().jobFilters;

      if (!isLoadMore && params.filters) {
        set({ jobFilters: params.filters });
        activeFilters = params.filters;
      }

      const { data, error } = await supabase.rpc('admin_fetch_jobs', {
        p_limit: limit,
        p_offset: offset,
        p_status: activeFilters.status || null,
        p_visibility: activeFilters.visibility || null,
        p_search: activeFilters.search || null,
        p_publication_state: activeFilters.publication || null,
      });

      if (error) throw error;

      const rows = (data || []) as AdminRpcRow<Job>[];
      const totalCount = rows[0]?.total_count || 0;
      const transformedJobs = rows.map(row => row.item) as Job[];

      set(state => ({
        jobs: isLoadMore ? [...state.jobs, ...transformedJobs] : transformedJobs,
        jobsOffset: offset + limit,
        hasMore: { ...state.hasMore, jobs: totalCount > offset + limit },
      }));
      return true;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return false;
    }
  },

  fetchJobById: async (jobId: string) => {
    const { data, error } = await supabase.rpc('admin_fetch_jobs', {
      p_limit: 1,
      p_offset: 0,
      p_job_id: jobId,
    });

    if (error) throw error;

    const rows = (data || []) as AdminRpcRow<AdminShadowJob>[];
    return rows[0]?.item ?? null;
  },

  fetchCreditTransactions: async (params: FetchCreditTransactionParams = {}) => {
    try {
      const { limitCount, offsetCount, isLoadMore = false } = params;
      const limit = limitCount || 10;
      const offset = offsetCount || 0;

      let activeFilters = params.filters || get().creditTransactionFilters;

      if (!isLoadMore && params.filters) {
        set({ creditTransactionFilters: params.filters });
        activeFilters = params.filters;
      }

      const { data, error } = await supabase.rpc('admin_fetch_credit_transactions', {
        p_limit: limit,
        p_offset: offset,
        p_search: activeFilters.search.trim() || null,
        p_action_type: activeFilters.actionType || null,
        p_start_date: activeFilters.startDate || null,
        p_end_date: activeFilters.endDate || null,
        p_user_id: activeFilters.userId || null,
      });

      if (error) throw error;

      const rows = (data || []) as AdminRpcRow<AdminCreditTransaction>[];
      const totalCount = rows[0]?.total_count || 0;
      const transformedTransactions = rows.map(row => row.item);

      set(state => ({
        creditTransactions: isLoadMore
          ? [...state.creditTransactions, ...transformedTransactions]
          : transformedTransactions,
        creditTransactionsOffset: offset + limit,
        hasMore: { ...state.hasMore, creditTransactions: totalCount > offset + limit },
      }));
    } catch (error) {
      console.error('Error fetching credit transactions:', error);
    }
  },

  fetchShadowRecruiters: async () => {
    try {
      const { data, error } = await supabase.rpc('admin_fetch_shadow_recruiters', {
        p_limit: 100,
        p_offset: 0,
        p_claimed: false,
      });

      if (error) throw error;

      set({
        shadowRecruiters:
          extractAdminRpcItems<AdminShadowRecruiter>(data).map(normalizeShadowRecruiter),
      });
    } catch (error) {
      console.error('Error fetching shadow recruiters:', error);
    }
  },

  // Méthodes pour charger plus de données
  fetchMoreIndustries: async () => {
    const { industriesOffset, limit, fetchIndustries } = get();
    await fetchIndustries({ offsetCount: industriesOffset, limitCount: limit });
  },

  fetchMoreCities: async () => {
    const { citiesOffset, limit, fetchCities, citiesSearch } = get();
    await fetchCities({
      offsetCount: citiesOffset,
      limitCount: limit,
      search: citiesSearch,
    });
  },

  fetchMoreServiceCategories: async () => {
    const { serviceCategoriesOffset, limit, fetchServiceCategories } = get();
    await fetchServiceCategories({ offsetCount: serviceCategoriesOffset, limitCount: limit });
  },

  fetchMoreProfiles: async () => {
    const { profilesOffset, fetchProfiles } = get();

    await fetchProfiles({
      offsetCount: profilesOffset,
      isLoadMore: true,
    });
  },

  fetchMoreReports: async () => {
    const { reportsOffset, limit, fetchReports } = get();
    await fetchReports({ offsetCount: reportsOffset, limitCount: limit });
  },

  fetchMoreReviews: async () => {
    const { reviewsOffset, limit, fetchReviews } = get();
    await fetchReviews({ offsetCount: reviewsOffset, limitCount: limit });
  },

  fetchMoreJobs: async () => {
    const { jobsOffset, fetchJobs } = get();

    await fetchJobs({
      offsetCount: jobsOffset,
      isLoadMore: true,
    });
  },

  fetchMoreCreditTransactions: async () => {
    const { creditTransactionsOffset, fetchCreditTransactions } = get();

    await fetchCreditTransactions({
      offsetCount: creditTransactionsOffset,
      isLoadMore: true,
    });
  },

  // Actions administratives
  updateReportStatus: async (reportId: string, status: Report['status'], adminNotes?: string) => {
    try {
      const { fetchReports } = get();

      const { error } = await supabase.rpc('admin_update_report_status', {
        p_report_id: reportId,
        p_status: status,
        p_admin_notes: adminNotes ?? null,
      });

      if (error) throw error;

      await fetchReports();
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  },

  updateProfileRole: async (profileId: string, role: string) => {
    try {
      const { fetchProfiles } = get();
      const { error } = await supabase.rpc('admin_update_profile_role', {
        p_profile_id: profileId,
        p_role: role,
      });

      if (error) throw error;

      // Recharger les profils
      await fetchProfiles();
    } catch (error) {
      console.error('Error updating profile role:', error);
      throw error;
    }
  },

  updateProfileStatus: async (profileId: string, status: string) => {
    try {
      const { error } = await supabase.rpc('admin_update_profile_status', {
        p_profile_id: profileId,
        p_status: status,
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating profile status:', error);
      throw error;
    }
  },

  anonymizeUser: async (profileId: string) => {
    const { error } = await supabase.functions.invoke('delete-user', {
      body: { profileId },
      method: 'POST',
    });

    if (error) {
      throw error;
    }

    await get().fetchProfiles();
  },

  lookupUserByEmail: async (email: string) => {
    const { data, error } = await supabase.rpc('admin_lookup_user_by_email', { p_email: email });
    if (error) throw error;
    return data;
  },

  generateAuthLink: async (userId: string) => {
    const { data, error } = await supabase.functions.invoke('generate-auth-link', {
      body: { userId },
      method: 'POST',
    });
    if (error) throw error;
    return data as { actionLink: string; linkType: 'magiclink' | 'recovery' };
  },

  updateObnlReviewStatus: async (profileId: string, reviewStatus: ObnlReviewStatus) => {
    try {
      const { error } = await supabase.rpc('admin_update_obnl_review_status', {
        p_profile_id: profileId,
        p_status: reviewStatus,
      });

      if (error) throw error;

      set(state => ({
        profiles: state.profiles.map(profile =>
          profile.id === profileId
            ? {
                ...profile,
                obnl_review_status: reviewStatus,
              }
            : profile
        ),
      }));
    } catch (error) {
      console.error('Error updating OBNL review status:', error);
      throw error;
    }
  },

  updateReviewStatus: async (reviewId: string, validated: boolean) => {
    try {
      const { fetchReviews } = get();

      const { error } = await supabase.rpc('admin_update_review_status', {
        p_review_id: reviewId,
        p_validated: validated,
      });

      if (error) throw error;

      await fetchReviews();
    } catch (error) {
      console.error('Error updating review status:', error);
      throw error;
    }
  },

  updateJobStatus: async (jobId: string, status: string) => {
    try {
      const { fetchJobs } = get();

      const { error } = await supabase.rpc('admin_update_job_status', {
        p_job_id: jobId,
        p_status: status,
      });

      if (error) throw error;

      await fetchJobs();
    } catch (error) {
      console.error('Error updating job status:', error);
      throw error;
    }
  },

  updateJobExpiresAt: async (jobId: string, expiresAt: string) => {
    const { data, error } = await supabase.rpc('admin_update_job_expires_at', {
      p_job_id: jobId,
      p_expires_at: expiresAt,
    });

    if (error) throw error;

    const update = extractAdminJobDeadlineUpdate(data);
    if (!update) {
      await get().fetchJobs();
      return;
    }

    set(state => ({
      jobs: state.jobs.map(job => (job.id === jobId ? { ...job, ...update } : job)),
    }));
  },

  updateJobSelectionDate: async (jobId: string, selectionDate: string | null) => {
    const { data, error } = await supabase.rpc('admin_update_job_selection_date', {
      p_job_id: jobId,
      p_selection_date: selectionDate,
    });

    if (error) throw error;

    const update = extractAdminJobDeadlineUpdate(data);
    if (!update) {
      await get().fetchJobs();
      return;
    }

    set(state => ({
      jobs: state.jobs.map(job => (job.id === jobId ? { ...job, ...update } : job)),
    }));
  },

  upsertShadowRecruiter: async input => {
    const { data, error } = await supabase.rpc('admin_upsert_shadow_recruiter', {
      p_shadow_recruiter_id: input.id ?? null,
      p_first_name: input.first_name.trim(),
      p_last_name: input.last_name.trim(),
      p_email: input.email?.trim() || null,
      p_company_name: input.company_name?.trim() || null,
      p_job_ids: input.job_ids ?? [],
    });

    if (error) throw error;

    const recruiter = normalizeShadowRecruiter(Array.isArray(data) ? data[0] : data);

    await get().fetchShadowRecruiters();
    await get().fetchJobs({
      filters: get().jobFilters,
      offsetCount: 0,
      isLoadMore: false,
    });

    return recruiter;
  },

  issueShadowRecruiterInvite: async shadowRecruiterId => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : null;
    const { data, error } = await supabase.rpc('admin_issue_shadow_recruiter_invite', {
      p_shadow_recruiter_id: shadowRecruiterId,
      p_base_url: baseUrl,
    });

    if (error) throw error;

    const invite = normalizeShadowRecruiterInvite(
      shadowRecruiterId,
      Array.isArray(data) ? data[0] : data
    );

    set(state => ({
      shadowRecruiterInvite: invite,
      shadowRecruiters: state.shadowRecruiters.map(recruiter =>
        recruiter.id === shadowRecruiterId ? { ...recruiter, latest_invite: invite } : recruiter
      ),
    }));

    return invite;
  },

  adminUpsertJob: async (jobId: string, params: JobUpsertParams, reason: string) => {
    const { error } = await supabase.rpc('admin_upsert_job', {
      p_job_id: jobId,
      p_title: params.title,
      p_city_id: params.city.id,
      p_description: params.description,
      p_budget_range_id: params.budgetRange.id,
      p_service_category_ids: params.requiredServices.map(s => s.id),
      p_on_site: params.onSite,
      p_languages: params.languages,
      p_start_date: params.startDate,
      p_end_date: params.endDate || null,
      p_reason: reason.trim(),
    });

    if (error) throw error;

    await get().fetchJobs();
  },

  adminUnpublishJob: async (jobId: string, reason: string) => {
    const { data, error } = await supabase.rpc('admin_unpublish_job', {
      p_job_id: jobId,
      p_reason: reason.trim(),
    });

    if (error) throw error;

    const action = data as 'pause' | 'unpublish';
    await get().fetchJobs();
    return action;
  },

  adminPublishJob: async (
    jobId: string,
    listingTypeId: number | null,
    expiresAt: string | null,
    selectionDate: string | null = null
  ) => {
    const { error } = await supabase.rpc('admin_publish_job', {
      p_job_id: jobId,
      p_listing_type_id: listingTypeId,
      p_expires_at: expiresAt,
      p_selection_date: selectionDate,
    });

    if (error) throw error;

    // The publish itself is already committed once we reach this point, so a
    // failed refresh must not be reported to the caller as a publish failure
    // (that would invite a retry that immediately fails with
    // JOB_ALREADY_PUBLISHED). fetchJobs resolves to false rather than
    // rejecting on failure, so the caller can distinguish "published, list
    // refreshed" from "published, refresh failed" and message accordingly.
    return get().fetchJobs();
  },

  searchRecruiterProfiles: async (search: string) => {
    const { data, error } = await supabase.rpc('admin_fetch_profiles', {
      p_limit: 10,
      p_offset: 0,
      p_role: 'recruiter',
      p_status: 'active',
      p_city: null,
      p_obnl_review_status: null,
      p_search: search.trim() || null,
    });

    if (error) throw error;

    // Intentionally does not `set({ profiles })`: that slice belongs to
    // fetchProfiles / the admin profile list page, and clobbering it from a
    // modal's typeahead would corrupt whatever list that page is showing.
    return extractAdminRpcItems<AdminRecruiterOption>(data);
  },

  assignJobToRecruiter: async (jobId: string, profileId: string) => {
    const { error } = await supabase.rpc('admin_assign_jobs_to_recruiter', {
      p_job_id: jobId,
      p_profile_id: profileId,
    });

    if (error) throw error;

    // Same "commit already happened, refresh failure just returns false"
    // pattern as adminPublishJob: the transfer is committed by this point,
    // so a failed refresh must not be reported to the caller as a failed
    // assignment.
    const jobsRefreshed = await get().fetchJobs();

    // The shadowRecruiters slice (used by ShadowRecruiterManagerModal) can
    // go stale after a transfer: a shadow recruiter placeholder that used to
    // list/count this job needs to reflect that it no longer does. This
    // refresh is best-effort on top of the already-committed transfer above
    // — fetchShadowRecruiters swallows its own errors internally, so it
    // never rejects and never changes what this method resolves to.
    await get().fetchShadowRecruiters();

    return jobsRefreshed;
  },

  forceNotifyMatchingFreelancers: async (jobId: string) => {
    const { data, error } = await supabase.rpc('admin_force_notify_matching_freelancers', {
      p_job_id: jobId,
    });
    if (error) throw error;
    return data as number;
  },

  approveSuggestedCategory: async (categoryId, labels) => {
    const { data, error } = await supabase.rpc('approve_suggested_category', {
      category_id: categoryId,
      p_label_fr: labels?.labelFr ?? null,
      p_label_en: labels?.labelEn ?? null,
    });

    if (error) {
      throw error;
    }

    const updatedCategory = data as ServiceCategory;

    set(state => ({
      serviceCategories: state.serviceCategories.map(category =>
        category.id === categoryId ? { ...category, ...updatedCategory } : category
      ),
    }));

    return updatedCategory;
  },

  createAdminProfile: async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    languages: string[],
    cityId: number,
    role: string,
    avatarUrl?: string,
    phoneNumber?: string
  ): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('add-admin', {
        body: {
          email: email,
          phone: phoneNumber,
          password: password,
          first_name: firstName,
          last_name: lastName,
          city_id: cityId,
          languages: languages,
          avatar_url: avatarUrl,
          role: role,
        },
        method: 'POST',
      });

      if (error) {
        throw error;
      }
      const createdUserId = data.userId;
      return createdUserId;
    } catch (error) {
      console.error('Failed to create admin profile', error);
      throw error;
    }
  },

  deleteServiceCategory: async (categoryId: number) => {
    const { error } = await supabase.rpc('admin_soft_delete_service_category', {
      p_category_id: categoryId,
    });

    if (error) {
      throw error;
    }

    // Update local state
    await get().fetchServiceCategories();
  },

  updateServiceCategory: async (categoryId, updates) => {
    const { data, error } = await supabase.rpc('admin_update_service_category', {
      p_category_id: categoryId,
      p_label_fr: updates.labelFr,
      p_label_en: updates.labelEn ?? null,
      p_description: updates.description ?? null,
    });

    if (error) {
      throw error;
    }

    const updatedCategory = data as ServiceCategory;

    set(state => ({
      serviceCategories: state.serviceCategories.map(category =>
        category.id === categoryId ? { ...category, ...updatedCategory } : category
      ),
    }));

    return updatedCategory;
  },

  deleteIndustry: async (id: number) => {
    const { error } = await supabase.rpc('admin_soft_delete_industry', {
      p_industry_id: id,
    });

    if (error) {
      throw error;
    }

    // Update local state
    await get().fetchIndustries();
  },

  insertCity: async ({ name, code, latitude, longitude }) => {
    const { error } = await supabase.rpc('admin_insert_city', {
      p_name: name,
      p_code: code,
      p_latitude: latitude,
      p_longitude: longitude,
    });

    if (error) {
      throw error;
    }
  },
}));
